import { FormEvent, FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useRequestCheckoutContext } from '../../../CheckoutContext/RequestContext';
import {
    CourseDurationPricing,
    RequestAdditionalConfig,
    RequestCheckoutState,
    SelectedOffers,
    SelectedPricing,
} from '@shared/types';
import { StepContainer } from '../../../StepContainer';
import styles from './styles.module.scss';
import { DurationSelector } from './DurationSelector';
import { DeliverySelector } from './DeliverySelector';
import { PricingSelector } from './PricingSelector';
import { isOfferDateValid } from '../../../shared/isOfferDateValid';
import { PublicOnRequestCourse } from '@cademy-package/sdk-component-types';

const calculateSpacesForOffers = (
    offers: SelectedOffers,
    course: PublicOnRequestCourse
): number => {
    const pricingOption = course.pricing_option;
    if (pricingOption.type !== 'price-offers') {
        throw new Error('Invalid state');
    }
    return Object.entries(offers).reduce((spaces, [offerId, quantity]) => {
        const offer = pricingOption.offers.find((offer) => offer._id === offerId);
        if (!offer) {
            throw new Error('Invalid state');
        }
        return spaces + (offer.spaces || 1) * quantity;
    }, 0);
};

export type DeliveryStepProps = {};

export const DeliveryStep: FunctionComponent<DeliveryStepProps> = () => {
    const { course, educator, checkoutState, submitResponse, durationOptions } =
        useRequestCheckoutContext();

    const initialPricing = useMemo((): SelectedPricing => {
        if (checkoutState.stepState.delivery.state?.pricing) {
            return checkoutState.stepState.delivery.state.pricing;
        }

        if (course.pricing_option.type === 'price-offers') {
            const firstOffer = course.pricing_option.offers[0];
            return {
                type: 'offers',
                addOns: {},
                offers: {
                    [firstOffer._id]: isOfferDateValid(firstOffer) ? 1 : 0,
                },
            };
        }

        return { type: 'spaces', quantity: 1 };
    }, [checkoutState, course.pricing_option]);

    const initialDelivery =
        useMemo((): Required<RequestAdditionalConfig>['on_request_delivery'] => {
            if (checkoutState.stepState.delivery.state?.delivery) {
                return checkoutState.stepState.delivery.state.delivery;
            }

            if (course.available_for_online_delivery) {
                return { type: 'Online' };
            }

            if (
                course.available_for_in_person_delivery &&
                course.educator_travel_settings.enabled
            ) {
                return { type: 'Educator travels', address: {} };
            }

            if (course.available_for_in_person_delivery && course.student_travel_settings.enabled) {
                return {
                    type: 'Student travels',
                    location_ref: course.student_travel_settings.locations[0],
                };
            }

            throw new Error('No delivery options available');
        }, [checkoutState, course]);

    const initialDuration = useMemo((): CourseDurationPricing => {
        if (checkoutState.stepState.delivery.state?.selectedDurationId) {
            const found = durationOptions.find(
                (duration) =>
                    duration._id === checkoutState.stepState.delivery.state?.selectedDurationId
            );
            if (found) {
                return found;
            }
        }
        return durationOptions[0];
    }, [checkoutState, durationOptions]);

    const [duration, setDuration] = useState<CourseDurationPricing>(initialDuration);
    const [delivery, setDelivery] =
        useState<Required<RequestAdditionalConfig>['on_request_delivery']>(initialDelivery);
    const [pricing, setPricing] = useState<SelectedPricing>(initialPricing);
    const hidePricing = useMemo(
        () => checkoutState.origin !== 'marketplace' && educator.feature_flags?.hide_pricing,
        [checkoutState.origin, educator.feature_flags]
    );

    const onContinue = useCallback(() => {
        submitResponse({
            actionType: 'request delivery configured',
            payload: {
                selectedDurationId: duration._id,
                delivery: delivery,
                pricing: pricing,
            },
        });
    }, [delivery, duration, pricing, submitResponse]);

    const onBackClick = useCallback(() => {
        return submitResponse({ actionType: 'go back', payload: {} });
    }, [submitResponse]);

    const requestedSpaces = useMemo(() => {
        if (pricing.type === 'spaces') {
            return pricing.quantity;
        }
        return calculateSpacesForOffers(pricing.offers, course);
    }, [pricing, course]);

    const optimisticCheckoutState = useMemo((): RequestCheckoutState => {
        return {
            ...checkoutState,
            stepState: {
                ...checkoutState.stepState,
                delivery: {
                    status: 'complete',
                    state: {
                        selectedDurationId: duration._id,
                        delivery: delivery,
                        requestedSpaces,
                        pricing: pricing,
                        payableCost: 0,
                    },
                },
            },
        };
    }, [checkoutState, delivery, duration, pricing, requestedSpaces]);

    const onFormSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
    }, []);

    return (
        <form onSubmit={onFormSubmit}>
            <StepContainer
                course={course}
                optimisticCheckoutState={optimisticCheckoutState}
                onContinueClick={onContinue}
                onBackClick={onBackClick}
            >
                <div className={styles.container}>
                    <PricingSelector
                        pricingOption={course.pricing_option}
                        selected={pricing}
                        hidePricing={hidePricing}
                        setSelected={setPricing}
                        currency={course.currency}
                    />

                    <DeliverySelector
                        course={course}
                        value={delivery}
                        onChange={setDelivery}
                        educatorName={educator.name}
                    />

                    <DurationSelector
                        durationPricing={durationOptions}
                        value={duration}
                        onChange={setDuration}
                    />
                </div>
            </StepContainer>
        </form>
    );
};
